import { useMemo } from 'react';
import { MetricGridView } from '@/components/views/MetricGridView';
import { ChartView } from '@/components/views/chart/ChartView';
import { aggregate, applyFilters, chartData, type AggregatableObject } from '@shared/app-aggregates';
import type { AppBlock, AppField } from '@shared/types/app-spec';
import { CHART_MAX_SERIES, type ChartViewData, type MetricGridViewData } from '@shared/types/views';
import type { AppCollection } from '@/lib/app-records';

const format = (value: number | null) => value === null ? '—' : value.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

/** A status key reads as its label; other values as they are. */
export const valueLabel = (collection: AppCollection) => (field: AppField, value: string) =>
  field === 'status' ? collection.statuses.find((status) => status.key === value)?.label ?? value : value;

/** F6b: figures computed from the objects the reader sees (archived ones left out). */
export const AppMetricsBlock = ({ block, objects }: { block: Extract<AppBlock, { type: 'metrics' }>; objects: AggregatableObject[] }) => {
  const data = useMemo<MetricGridViewData>(() => ({
    presentation: 'band',
    ...(block.label ? { title: block.label } : {}),
    metrics: block.items.map((item) => ({ label: item.label, value: format(aggregate(applyFilters(objects, item.filter), item.aggregate)) })),
  }), [block, objects]);
  return <MetricGridView data={data} hideTitle={!block.label} />;
};

/** F6b: bars or a line over the list, drawn by Domino's chart view. */
export const AppChartBlock = ({ block, objects, collection }: { block: Extract<AppBlock, { type: 'chart' }>; objects: AggregatableObject[]; collection: AppCollection }) => {
  const data = useMemo<ChartViewData>(() => {
    const chart = chartData(objects, block, valueLabel(collection));
    const series = chart.series.slice(0, CHART_MAX_SERIES);
    return {
      title: block.label, kind: block.kind, categories: chart.categories, series,
      ...(block.unit ? { unit: block.unit } : {}),
      ...(chart.series.length > series.length ? { source_note: `${series.length} premières séries sur ${chart.series.length}.` } : {}),
      empty_message: 'Pas encore de données pour ce graphique.',
    };
  }, [block, objects, collection]);
  return <div className="view-container rounded-2xl border border-domino-cardBorder bg-domino-white p-4"><ChartView data={data} /></div>;
};
